"use client";

import { useState } from "react";
import { useRecorderTeardown } from "./useRecorderTeardown";

/**
 * Opus-in-WebM where the browser supports it. The Blob assembled in `onstop`
 * must be tagged with the same type the recorder was constructed with, which is
 * why the choice is made once per recording rather than read from a constant.
 */
function preferredMimeType(): string {
  return MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
    ? "audio/webm;codecs=opus"
    : "audio/webm";
}

/**
 * A single microphone turn: request the mic, record until stopped or until
 * `maxRecordingSeconds` elapses, then hand the caller one Blob.
 *
 * The callers differ only in what they do with the Blob and how they surface a
 * denied microphone, so those are the two callbacks. Everything else - the mime
 * negotiation, chunk accumulation, releasing the getUserMedia tracks, the
 * duration timer, and the unmount latch from {@link useRecorderTeardown} - is
 * the same recording, and lives here.
 */
export function useAudioRecorder({
  maxRecordingSeconds,
  onRecordingComplete,
  onError,
  onTeardown,
}: {
  maxRecordingSeconds: number;
  onRecordingComplete: (blob: Blob) => void | Promise<void>;
  onError: () => void;
  onTeardown?: () => void;
}): {
  isRecording: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
} {
  const { mediaRecorderRef, chunksRef, timerRef, cancelledRef } = useRecorderTeardown(onTeardown);
  const [isRecording, setIsRecording] = useState(false);

  function stopRecording() {
    if (timerRef.current) clearTimeout(timerRef.current);
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = preferredMimeType();
      const mr = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        // Unmounted mid-recording: mic is released above; don't upload or
        // touch state for a gone component.
        if (cancelledRef.current) return;
        void onRecordingComplete(new Blob(chunksRef.current, { type: mimeType }));
      };

      mr.start();
      mediaRecorderRef.current = mr;
      setIsRecording(true);
      timerRef.current = setTimeout(() => stopRecording(), maxRecordingSeconds * 1000);
    } catch {
      onError();
    }
  }

  return { isRecording, startRecording, stopRecording };
}
